/**
 * The built-in copywriting instruction Signalist suggests per list source type (PREC-3687 §4.1).
 *
 * The Outreach step's instruction box starts empty, and an empty box is the one state where the
 * copywriting agents have nothing to go on but the signal. These bodies are the starting point for
 * the list types whose leads arrive with a *trigger* — the ones where outreach is the point of the
 * list rather than an afterthought to a campaign.
 *
 * Shared because both apps need the same text: the API seeds a **new** list's instruction with it
 * when the company has nominated no default template of its own
 * (`list/outreach/list-outreach-prefill.ts`), and the web offers it as a *Suggested* entry in the
 * Templates dropdown so an existing list is one click away from the same text.
 *
 * Two deliberate properties of the bodies:
 *
 * - **No variables.** The instruction goes through the campaign path's substitution, where a token
 *   with no value renders blank — so a `{prospect_firstName}` in text nobody proof-read would
 *   silently leave a hole. The agents already receive the lead's data as context; these bodies say
 *   what to make of it, which is what the field is for.
 * - **English only.** The body is guidance for the agent, not copy for the lead: the language a
 *   message is written in comes from the list's own language setting. Only the *names* are
 *   translated, and they live in the web's i18n bundles under the keys below.
 */

/** One suggested instruction: the i18n key of its display name, and the body itself. */
export interface SuggestedCopywritingInstruction {
	/** Key under `list.outreach.general.instruction.suggestions` in the web i18n bundles. */
	nameKey: string;
	/** The instruction text, copied verbatim into the list's `copywritingInstruction`. */
	body: string;
}

/**
 * Keyed by `ListSourceType` (spelled as strings — the enum lives in the API and this package sits
 * below both apps). A source type that is absent has no suggestion: its lists keep starting empty,
 * which is the correct answer for a list whose leads share no trigger to write from.
 */
export const SUGGESTED_COPYWRITING_INSTRUCTIONS: Record<string, SuggestedCopywritingInstruction> = {
	TRACKER: {
		nameKey: "latestAccountSignal",
		body: [
			"Pick the single strongest signal on this lead and write to that one.",
			"",
			"Ignore signals older than 90 days unless nothing newer exists. If the signal is about",
			"the company rather than the person, connect it to what someone in their role would make",
			"of it.",
		].join("\n"),
	},
	LI_COMPANY_ACTIVITY: {
		nameKey: "newCompanyFollower",
		body: [
			"They started following our company page.",
			"",
			"This is a light signal, not a buying signal. Acknowledge it once, briefly, and lead with",
			"something useful rather than a pitch. Do not thank them for the follow.",
		].join("\n"),
	},
	LI_PROFILE_VISITS: {
		nameKey: "profileVisitor",
		body: [
			"They looked at our profile, so there is mild interest but no stated need.",
			"",
			"Do not open with the visit itself — it reads as surveillance. Open on what someone in",
			"their role would have been looking for, and make the ask small.",
		].join("\n"),
	},
	LI_POST: {
		nameKey: "postEngagement",
		body: [
			"They engaged with a post on a specific topic.",
			"",
			"Write to the topic and the position the post takes, not to the like or the comment",
			"itself. If their comment carries an opinion, respond to that opinion.",
		].join("\n"),
	},
	JOB_CHANGE: {
		nameKey: "newInRole",
		body: [
			"They recently started a new role.",
			"",
			"Work out what changes in their first months — new scope, new team, whether the previous",
			"role prepared them for it — and write to that. Do not congratulate them; everyone else",
			"will. Check whether it is an internal move before assuming they are new to the company.",
		].join("\n"),
	},
	CSV_ENRICHMENTS: {
		nameKey: "uploadedList",
		body: [
			"These leads share the context they were uploaded with — an event, an export, a partner",
			"list.",
			"",
			"State that shared context in one line so the opening is not generic, then get to the",
			"point. They receive a lot of these; keep it to two or three sentences.",
		].join("\n"),
	},
	PARALLEL_AI: {
		nameKey: "monitoredCompanyNews",
		body: [
			"The company was surfaced by a monitored news or web signal.",
			"",
			"Do not summarise the news back to them — they know it. Work out what it most likely",
			"changes for the part of the business this person owns, and write to that implication.",
		].join("\n"),
	},
};

/**
 * The suggestion for a source type, or `null` when it has none.
 *
 * Null-tolerant on purpose: both callers hold a source type that the type system says is a string
 * but the database says may be missing on an old row.
 */
export function getSuggestedCopywritingInstruction(
	sourceType: string | null | undefined
): SuggestedCopywritingInstruction | null {
	if (!sourceType) {
		return null;
	}

	return SUGGESTED_COPYWRITING_INSTRUCTIONS[sourceType] ?? null;
}
